<template>
  <ModalCard>
    <template #body>
      <LoadingView v-if="isLoading" :active="isLoading" :is-full-page="true" />
      <section class="container">
        <p
          class="has-text-primary has-text-centered is-size-1 m-b-15 m-t-15 has-text-weight-semibold"
        >
          {{ $t('selfserve.upgrade-to-get-support') }}
        </p>
        <p class="m-b-30 is-size-4 has-text-centered">
          {{ $t('selfserve.currently-on-online-will') }}
        </p>
        <LightDiagnostic
          v-if="showLightDiagnostic"
          :show-billing-plan="true"
          :jurisdiction-id="currentEstate.jurisdictionId"
          :has-title-primary="false"
          :has-early-exit="true"
          :has-call-to-action="true"
          @custom-flow-change="back"
          @on-plan-selected="onPlanClicked"
        />
        <div v-else-if="!showLightDiagnostic" class="has-text-centered">
          <LightDiagnosticStarterBox @on-match-clicked="onMatchClick" />
          <p class="is-size-2 has-text-weight-semibold m-t-30">
            {{ $t('selfserve.guided-estate-plans') }}
          </p>
          <p class="is-size-4 m-t-10 m-b-10 px-6">
            {{ $t('selfserve.get-help-to-create-your-legal-documents') }}
          </p>
          <div class="is-flex-centered">
            <FormButtonToggle
              v-model="planParty"
              class="m-b-30 m-t-10"
              :values="planPartyTypes"
              value-by-prop="description"
              :data-e2e="LightDiagnosticCypressAttribute.buttons.COUPLE_TYPE_TOGGLE"
              @select="onCoupleTypeSelect"
            />
          </div>
          <div class="columns">
            <div class="column is-4" v-for="plan in plans" :key="plan.type">
              <VaultCompactPlanBillingCard
                :selected-jurisdiction="currentEstate.jurisdictionId"
                :billing-plan="plan"
                @on-plan-clicked="onPlanClicked"
              >
                <template #inclusions-exclusions="{ billingPlan }">
                  <BillingCardInclusionsExclusions
                    class="has-text-left"
                    :is-horizontal="false"
                    :show-subtitles="false"
                    inclusion-icon="check-circle-1-filled"
                    exclusion-icon="remove-circle"
                    :inclusions="billingPlan.includedUpsells"
                    :exclusions="billingPlan.excludedUpsells"
                  />
                </template>
              </VaultCompactPlanBillingCard>
            </div>
          </div>
        </div>
      </section>
    </template>
  </ModalCard>
</template>

<script>
import ModalCard from '@/modules/core/ui/components/ModalCard.vue';
import LightDiagnosticStarterBox from '@/modules/lightdiagnostic/components/presentational/LightDiagnosticStarterBox.vue';
import { VaultBillingPlanFormFactory } from '@/modules/estate/models/VaultBillingPlanFormFactory';
import { BillingPlanId } from '@/modules/billing/constants/BillingPlanId';
import VaultCompactPlanBillingCard from '@/modules/estate/components/presentational/VaultCompactPlanBillingCard.vue';
import FormButtonToggle from '@/modules/core/form/components/FormButtonToggle.vue';
import { LightDiagnosticCypressAttribute } from '@/modules/lightdiagnostic/constants/LightDiagnosticCypressAttribute';
import { EstatePlanPartyType } from '@/modules/estate/enums/EstatePlanPartyType';
import LightDiagnostic from '@/modules/lightdiagnostic/components/stateful/LightDiagnostic.vue';
import { HasCurrentEstateMixin } from '@/modules/core/app/mixins/HasCurrentEstateMixin';
import SelfServeCheckoutSessionUIService from '@/modules/billing/services/SelfServeCheckoutSessionUIService';
import { ModalMixin } from '@/modules/core/ui/mixins/ModalMixin';
import { ProductKey } from '@/modules/estate/enums/ProductKey';
import { OpensFreeConsultModalMixin } from '@/modules/meeting/components/mixins/OpensFreeConsultModalMixin';
import { EstateRoute } from '@/modules/estate/constants/EstateRoute';
import { LightDiagnosticOriginType } from '@/modules/lightdiagnostic/constants/LightDiagnosticOriginType';
import LightDiagnosticService from '@/modules/lightdiagnostic/services/LightDiagnosticService';
import { HasAuthUserMixin } from '@/modules/core/app/mixins/HasAuthUserMixin';
import LoadingView from '@/modules/core/ui/components/wrappers/LoadingView.vue';
import BillingCardInclusionsExclusions from '@/modules/estate/components/presentational/BillingCardInclusionsExclusions.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'SelfServeUpgradeModal',
  mixins: [HasAuthUserMixin, HasCurrentEstateMixin, ModalMixin, OpensFreeConsultModalMixin],
  components: {
    LightDiagnostic,
    FormButtonToggle,
    VaultCompactPlanBillingCard,
    ModalCard,
    LightDiagnosticStarterBox,
    LoadingView,
    BillingCardInclusionsExclusions
  },
  static: {
    LightDiagnosticCypressAttribute,
    planPartyTypes: EstatePlanPartyType.getValues()
  },
  data() {
    return {
      plans: [],
      isLoading: false,
      showLightDiagnostic: false,
      planParty: EstatePlanPartyType.INDIVIDUAL.key
    };
  },
  computed: {
    subTitle() {
      switch (this.currentEstate.billing?.billingPlanMapping?.planId) {
        case BillingPlanId.COUPLES_STANDARD_PLAN:
        case BillingPlanId.INDIVIDUAL_BASIC_PLAN:
          return this.$t('selfserve.currently-on-basic');
        default:
          return this.$t('selfserve.currently-on-online-will');
      }
    }
  },
  methods: {
    async onCoupleTypeSelect(value) {
      try {
        this.isLoading = true;
        const jurisdictionId = this.currentEstate.jurisdictionId;
        const companyId = this.currentEstate.company?.id;
        if (value === EstatePlanPartyType.INDIVIDUAL.key) {
          await Promise.all(
            this.plans.map((plan) => {
              return plan
                .setAsIndividual()
                .loadPlanPrices(
                  jurisdictionId,
                  companyId,
                  this.authUser.partnershipInvitation?.type
                );
            })
          );
        }
        if (value === EstatePlanPartyType.COUPLE.key) {
          await Promise.all(
            this.plans.map((plan) => {
              return plan
                .setAsCouples()
                .loadPlanPrices(
                  jurisdictionId,
                  companyId,
                  this.authUser.partnershipInvitation?.type
                );
            })
          );
        }
      } finally {
        this.isLoading = false;
      }
    },
    back() {
      this.showLightDiagnostic = false;
    },
    onMatchClick() {
      this.showLightDiagnostic = true;
    },
    onPlanClicked(billingPlan, lightDiagnosticForm) {
      if (lightDiagnosticForm) {
        this.syncToHubspot(billingPlan, lightDiagnosticForm);
      }

      switch (billingPlan.productKey) {
        case ProductKey.INDIVIDUAL_BASIC_PLAN:
          this.upgradeToIndividualBasic();
          break;
        case ProductKey.COUPLES_BASIC_PLAN:
          this.upgradeToCouplesBasic();
          break;
        case ProductKey.INDIVIDUAL_PROFESSIONAL_EXECUTOR_PLAN:
        case ProductKey.INDIVIDUAL_COMPLETE_PLAN:
          this.openFreeConsultModal(this.currentEstate.jurisdictionId);
      }
    },
    syncToHubspot(billingPlan, lightDiagnosticForm) {
      LightDiagnosticService.syncSubmissionToHubspot({
        goals: lightDiagnosticForm.goals,
        situations: lightDiagnosticForm.situations,
        recommendation: billingPlan.productKey,
        origin: LightDiagnosticOriginType.SELF_SERVE_UPGRADE,
        userId: this.authUser.id,
        estateId: this.currentEstate.id
      });
    },
    upgradeToIndividualBasic() {
      this.close();
      SelfServeCheckoutSessionUIService.showIndividualBasicPlanModal({
        parent: this,
        successUrl: `${window.location.origin}/${EstateRoute.DASHBOARD}?show_redirect=true`
      });
    },
    upgradeToCouplesBasic() {
      this.close();
      SelfServeCheckoutSessionUIService.showCouplesBasicPlanModal({
        parent: this,
        successUrl: `${window.location.origin}/${EstateRoute.DASHBOARD}?show_redirect=true`
      });
    },
    async buildPlans() {
      const jurisdictionId = this.currentEstate.jurisdictionId;
      const companyId = this.currentEstate.company?.id;

      try {
        this.isLoading = true;
        this.plans = [
          VaultBillingPlanFormFactory.build(BillingPlanId.INDIVIDUAL_BASIC_PLAN),
          VaultBillingPlanFormFactory.build(BillingPlanId.INDIVIDUAL_COMPLETE_PLAN),
          VaultBillingPlanFormFactory.build(BillingPlanId.INDIVIDUAL_PROFESSIONAL_EXECUTOR)
        ];

        await Promise.all(
          this.plans.map((plan) => {
            return plan.loadPlanPrices(
              jurisdictionId,
              companyId,
              this.authUser.partnershipInvitation?.type
            );
          })
        );
      } finally {
        this.isLoading = false;
      }
    }
  },
  async mounted() {
    await this.buildPlans();
  }
};
</script>

<style scoped>
.container {
  margin: auto;
  width: 90%;
}
</style>
